import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, PieChart } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminSectorBackfillButton — Round 95p (Jun 2026).
 *
 * ONE-TIME button: backfills `sector` from Massive SIC codes for every
 * stock_cache row that's missing one (or polluted with a raw ALL-CAPS
 * SIC description). ETFs/funds are skipped by design — they keep a
 * blank sector and bucket as "ETFs / Funds" in the allocation chart.
 *
 * Once the run completes, the backend writes a permanent marker and
 * this button disappears FOREVER (per user request).
 */
export default function AdminSectorBackfillButton() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/sector-backfill/status`, { withCredentials: true });
      setStatus(data);
    } catch { /* non-admin → hidden */ }
  }, []);

  useEffect(() => {
    if (user?.role !== 'admin') return undefined;
    fetchStatus();
    if (!status?.in_progress) return undefined;
    const id = setInterval(fetchStatus, 4000);
    return () => clearInterval(id);
  }, [user, fetchStatus, status?.in_progress]);

  // Round 97a (Feb 2026) — Was hidden forever once `status.done`
  // flipped true. That blocked the WEN/KBH re-pollution fix because
  // the button literally vanished from the admin panel after the
  // initial migration. New rule: visible to admin whenever the
  // status payload reports pending_targets > 0 (or while a run is
  // in progress so the progress label keeps updating).
  if (user?.role !== 'admin' || !status) return null;
  const hasTargets = (status.pending_targets || 0) > 0 || status.in_progress;
  if (!hasTargets) return null;

  const fire = async () => {
    if (busy || status.in_progress) return;
    const n = status.pending_targets ?? '?';
    if (!window.confirm(`Backfill sectors from Massive SIC codes for ${n} tickers? Runs in the background.`)) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/sector-backfill/fire`, {}, { withCredentials: true });
      if (data?.done) {
        toast.success('Nothing to backfill', { description: data.message });
      } else {
        toast.success('Sector backfill started', { description: `${data.total} tickers queued — runs in the background.` });
      }
      setTimeout(fetchStatus, 800);
    } catch (err) {
      toast.error('Could not start sector backfill', {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setBusy(false);
    }
  };

  const label = status.in_progress
    ? `Sector Backfill: ${Number(status.processed).toLocaleString()} / ${Number(status.total).toLocaleString()} · ${status.fixed} fixed`
    : `Admin: Backfill sectors from SIC${status.pending_targets != null ? ` · ${status.pending_targets} pending` : ''}`;

  return (
    <button
      type="button"
      onClick={status.in_progress ? fetchStatus : fire}
      disabled={busy}
      className="inline-flex items-center gap-1.5 text-[10px] font-semibold transition-colors px-2 py-1 rounded-md border border-dashed border-muted-foreground/30 text-teal-700 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-950/30 disabled:opacity-60"
      title="Maps Massive SIC codes to the app's 11 sector labels for every stock with a blank or polluted (SIC-description-leaked) sector. Re-runnable — surfaces whenever pending targets are detected."
      data-testid="admin-sector-backfill-btn"
    >
      {status.in_progress ? <Loader2 className="w-3 h-3 animate-spin" /> : <PieChart className="w-3 h-3" />}
      {label}
    </button>
  );
}
